import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiChevronDown, FiHelpCircle } = FiIcons;

/**
 * FAQ_DATA - 首頁常見問題資料
 * ✅ 同時供 FAQSection UI 組件 和 HomePage SEO Schema 使用
 * ✅ 移除死代碼：generateFAQPageSchema()（已由 src/lib/seo.js 的 generateFAQSchema 取代）
 */
export const FAQ_DATA = [
  {
    id: 1,
    question: 'Omakase 是什麼意思？',
    answer: 'Omakase（お任せ）是日文「我交給你」或「全憑您的意思」的意思。在餐廳用餐時，選擇 Omakase 代表您完全信任廚師，讓廚師根據當天最新鮮的食材和自己的創意，為您決定菜單內容。這是一種體驗廚師最高技藝的用餐方式，也是日本料理文化中最高層次的信任表現。',
  },
  {
    id: 2,
    question: 'Omakase 大概要花多少錢？',
    answer: '台灣的 Omakase 價格範圍很廣：入門級約 NT$ 1,500-3,000（午間套餐），中階約 NT$ 3,000-8,000，高端約 NT$ 8,000-20,000+。日本頂級 Omakase 可達每人 NT$ 30,000-100,000。價格主要取決於食材品質（是否使用日本直送食材）、廚師資歷、餐廳位置和服務水準。建議第一次體驗可從午間套餐開始，較親民也方便入門。',
  },
  {
    id: 3,
    question: '第一次去 Omakase 需要注意什麼？',
    answer: '第一次體驗 Omakase 有幾個重點：1) 提前預約，通常需要 1-4 週甚至更早；2) 告知飲食限制和過敏原；3) 準時或提前 5-10 分鐘到達；4) 穿著得體（Smart Casual 以上）；5) 用餐時盡量跟隨廚師節奏，壽司上桌後應立即品嚐；6) 可以向廚師詢問食材來源，這是體驗的一部分；7) 避免混合山葵和醬油；8) 手機調靜音，拍照時關閉閃光燈。',
  },
  {
    id: 4,
    question: '壽司 Omakase 和懷石 Omakase 有什麼不同？',
    answer: '壽司 Omakase 源自江戶時代，以新鮮海鮮和醋飯為核心，廚師在吧台即時捏製，互動性高，用餐時間約 1.5-2 小時。懷石 Omakase 源自茶道文化，追求完整的季節美學體驗，包含湯品、生魚片、燉煮、燒烤、炸物等多達 9-12 道料理，用餐時間約 2.5-3.5 小時，氛圍較為正式安靜。兩者都是頂級日本料理體驗，選擇取決於您的喜好和場合。',
  },
  {
    id: 5,
    question: '台灣哪裡可以體驗到好的 Omakase？',
    answer: '台灣 Omakase 文化發展蓬勃，主要集中在台北市（信義區、大安區、中山區）、新北市（板橋、新店）以及台中、高雄等城市。許多餐廳使用日本直送食材，部分則融入台灣本地優質食材如東港黑鮪魚、宜蘭海產等。建議透過美食評鑑網站、社群媒體或口碑推薦，選擇與您預算和口味相符的餐廳。',
  },
  {
    id: 6,
    question: 'Omakase 可以告知不吃的食材嗎？',
    answer: '完全可以，而且強烈建議在預約時就說明！無論是食物過敏、宗教飲食限制（如不吃豬肉、牛肉）、或個人偏好（如不吃生食、海膽、魚卵等），都應該提前告知餐廳。好的廚師會根據您的需求調整菜單，確保您有最好的體驗。Omakase 的精神是信任，廚師也希望了解您的需求，才能真正為您提供最適合的料理。',
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId(prev => (prev === id ? null : id));

  return (
    <section
      id="faq"
      className="py-24 relative z-10"
      aria-labelledby="faq-heading"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="container-custom">
        {/* 標題 */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-rose-500" />
            <h2 id="faq-heading" className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">
              常見問題
            </h2>
          </div>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            關於 Omakase 你最想知道的一切
          </p>
        </motion.div>

        {/* FAQ 列表 */}
        <div className="max-w-3xl mx-auto space-y-4">
          {FAQ_DATA.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <div className="glass-effect rounded-2xl overflow-hidden">
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center justify-between p-6 text-left focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-inset"
                  aria-expanded={openId === item.id}
                  aria-controls={`faq-answer-${item.id}`}
                >
                  <h3
                    className="text-lg font-semibold text-slate-900 dark:text-white pr-4"
                    itemProp="name"
                  >
                    {item.question}
                  </h3>
                  <motion.div
                    animate={{ rotate: openId === item.id ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-rose-500" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {openId === item.id && (
                    <motion.div
                      id={`faq-answer-${item.id}`}
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                    >
                      <div className="px-6 pb-6 border-t border-slate-200/50 dark:border-slate-700/50">
                        <p
                          className="text-slate-600 dark:text-slate-400 leading-relaxed pt-4"
                          itemProp="text"
                        >
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}